import React, { useState, useEffect } from 'react';
import PaymentSummary from '../../sitePages/PaymentSummary';
import { SortCodeInput } from '../SortCodeInput/SortCodeInput';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import { hasPaymentSelected } from '../ProductMethods';
import { PaymentMethod } from '../Constants';
import { DirectDebitDetails, ProductType, Result } from '../CommonTypes';
import directDebitLogo from '../../../images/directdebit.gif';
import { PaymentDay, ProductChannel } from '../../../consts/Common';
import { PaymentApi } from '../../../api/PaymentApi';
import Checkbox from '../../common/Checkbox/Checkbox';
import { CommonValidation } from '../CommonValidation/commonValidation';
import SelectInput, { SelectInputHelper } from '../SelectInput/SelectInput';
import LoadingContainer from '../LoadingContainer/LoadingContainer';
import TextInput from '../TextInput/TextInput';
import { useTranslation } from 'react-i18next';

export type DirectDebitErrors = {
  accountHolderName?: string;
  sortCode?: string;
  accountNumber?: string;
  bankValidation?: string;
  paymentDay?: string;
};

type DirectDebitProps = {
  products: ProductType[];
  initialDirectDebitDetails: DirectDebitDetails;
  validate: boolean;
  update: boolean;
  token: string;
  onValidated: (isValid: boolean) => void;
  onUpdateDirectDebitDetails: (
    canContinue: boolean,
    directDebitDetails: DirectDebitDetails
  ) => void;
  showLoading: boolean;
  loadingMessage?: string;
  displayCustomerFriendlyName?: boolean;
  productChannel: number;
  agreeDirectDebitTerms?: string;
  agreeDirectDebitDueDateTerms?: string;
  displaySendDDGinWritingTerms?: boolean;
  directDebitPayee: string;
  culture: string;
  externalLoadingPanel?: boolean;
};

const DirectDebit = ({
  products,
  initialDirectDebitDetails,
  validate,
  update,
  token,
  onValidated,
  onUpdateDirectDebitDetails,
  showLoading,
  loadingMessage,
  displayCustomerFriendlyName,
  productChannel,
  agreeDirectDebitTerms,
  agreeDirectDebitDueDateTerms,
  displaySendDDGinWritingTerms,
  directDebitPayee,
  culture,
  externalLoadingPanel
}: DirectDebitProps): JSX.Element => {
  const [directDebitErrors, setDirectDebitErrors] = useState<DirectDebitErrors>(
    {}
  );
  const [directDebitDetails, setDirectDebitDetails] =
    useState<DirectDebitDetails>(initialDirectDebitDetails);

  const [expanded, setExpanded] = useState(false);
  const [agreedDirectDebit, setAgreedDirectDebit] = useState(false);
  const [agreedDirectDebitDueDate, setAgreedDirectDebitDueDate] =
    useState(false);
  const [agreedSubscriptionPayment, setAgreedSubscriptionPayment] =
    useState(false);
  const [agreedSendDDGInWriting, setAgreedSendDDGInWriting] = useState(false);
  const [validateBankAccount, setValidateBankAccount] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    accountHolderName,
    sortCode1,
    sortCode2,
    sortCode3,
    accountNumber,
    selectedPaymentDay
  } = directDebitDetails;

  const lookUpDays = [
    { id: '1', value: 1, text: '1', disabled: false },
    { id: '2', value: 2, text: '2', disabled: false },
    { id: '3', value: 3, text: '3', disabled: false },
    { id: '4', value: 4, text: '4', disabled: false },
    { id: '5', value: 5, text: '5', disabled: false },
    { id: '6', value: 6, text: '6', disabled: false },
    { id: '7', value: 7, text: '7', disabled: false },
    { id: '8', value: 8, text: '8', disabled: false },
    { id: '9', value: 9, text: '9', disabled: false },
    { id: '10', value: 10, text: '10', disabled: false },
    { id: '11', value: 11, text: '11', disabled: false },
    { id: '12', value: 12, text: '12', disabled: false },
    { id: '13', value: 13, text: '13', disabled: false },
    { id: '14', value: 14, text: '14', disabled: false },
    { id: '15', value: 15, text: '15', disabled: false },
    { id: '16', value: 16, text: '16', disabled: false },
    { id: '17', value: 17, text: '17', disabled: false },
    { id: '18', value: 18, text: '18', disabled: false },
    { id: '19', value: 19, text: '19', disabled: false },
    { id: '20', value: 20, text: '20', disabled: false },
    { id: '21', value: 21, text: '21', disabled: false },
    { id: '22', value: 22, text: '22', disabled: false },
    { id: '23', value: 23, text: '23', disabled: false },
    { id: '24', value: 24, text: '24', disabled: false },
    { id: '25', value: 25, text: '25', disabled: false },
    { id: '26', value: 26, text: '26', disabled: false },
    { id: '27', value: 27, text: '27', disabled: false },
    { id: '28', value: 28, text: '28', disabled: false },
    { id: '29', value: 29, text: '29', disabled: false },
    { id: '30', value: 30, text: '30', disabled: false },
    { id: '31', value: 31, text: '31', disabled: false }
  ];

  const onChangeDirectDebitDetails = (
    value: string | number | null,
    key: string
  ) => {
    setDirectDebitDetails({
      ...directDebitDetails,
      [key]: value
    });
  };

  useEffect(() => {
    initializePaymentDay();
  }, []);

  const initializePaymentDay = () => {
    if (showPreferredPaymentDay()) {
      if (selectedPaymentDay === PaymentDay.Empty) {
        onChangeDirectDebitDetails(PaymentDay.Default, 'selectedPaymentDay');
      }
    } else {
      onChangeDirectDebitDetails(PaymentDay.Empty, 'selectedPaymentDay');
    }
  };

  const showPreferredPaymentDay = (): boolean => {
    const result = !products.some(
      (p) =>
        p.isSelected === true &&
        p.selectedPaymentMethod === PaymentMethod.Subscription
    );
    return result;
  };

  const onDirectDebitAgreementChange = () => {
    setAgreedDirectDebit(!agreedDirectDebit);
  };

  const onDirectDebitDueDateConfirmationChange = () => {
    setAgreedDirectDebitDueDate(!agreedDirectDebitDueDate);
  };

  const onAgreedSubscriptionPaymentChange = () => {
    setAgreedSubscriptionPayment(!agreedSubscriptionPayment);
  };

  const onAgreedSendDDGInWritingChange = () => {
    setAgreedSendDDGInWriting(!agreedSendDDGInWriting);
  };

  const onChangeDetail = (
    e: React.FormEvent<HTMLInputElement>,
    key: string
  ) => {
    onChangeDirectDebitDetails(e.currentTarget.value, key);
  };

  const onPaymentDayChange = (value: string) => {
    onChangeDirectDebitDetails(value, 'selectedPaymentDay');
  };

  const onChangeAccountNumber = (
    e: React.FormEvent<HTMLInputElement>,
    key: string
  ) => {
    const value = (e.target as HTMLInputElement).value;

    const newValue =
      CommonValidation.isEmpty(value) || isNaN(parseInt(value))
        ? null
        : getNumberValuesFromString(value);

    onChangeDirectDebitDetails(newValue, key);
  };

  const onChangeSortCode = (
    e: React.FormEvent<HTMLInputElement>,
    key: string
  ) => {
    const value = (e.target as HTMLInputElement).value;

    const newValue =
      CommonValidation.isEmpty(value) || isNaN(parseInt(value))
        ? null
        : getNumberValuesFromString(value);

    onChangeDirectDebitDetails(newValue, key);
  };

  const getNumberValuesFromString = (stringInput: string) => {
    const numberPattern = /\d+/g;
    const numberWithoutLetters = stringInput?.match(numberPattern)?.join('');

    return numberWithoutLetters ?? '';
  };
  const hasMonthly = hasPaymentSelected(products, PaymentMethod.Monthly);
  const hasSubscription = hasPaymentSelected(
    products,
    PaymentMethod.Subscription
  );

  useEffect(() => {
    let hasUserAgreed = true;
    if (!agreedDirectDebit) {
      hasUserAgreed = false;
    }

    if (hasMonthly && !agreedDirectDebitDueDate) {
      hasUserAgreed = false;
    }

    if (hasSubscription && !agreedSubscriptionPayment) {
      hasUserAgreed = false;
    }
    onUpdateDirectDebitDetails(hasUserAgreed, directDebitDetails);
  }, [agreedDirectDebit, agreedDirectDebitDueDate, agreedSubscriptionPayment]);

  useEffect(() => {
    if (update === true) {
      onUpdateDirectDebitDetails(true, directDebitDetails);
    }
  }, [update]);

  useEffect(() => {
    const controller = new AbortController();

    const validateBankDetails = () => {
      const successCallback = (data: Result) => {
        if (data.isSuccess) {
          onChangeDirectDebitDetails(data.text, 'bankName');
          setValidateBankAccount(false);
          onDetailsValidated(true);
        } else {
          setValidateBankAccount(false);
          const newError = {
            ...directDebitErrors,
            ['bankValidation']:
              t('customer-direct-debit-page.validation-failed') + data.text
          };
          setDirectDebitErrors(newError);
          !externalLoadingPanel && setLoading(false);
          onDetailsValidated(false);
        }
      };

      const failureCallback = (error: string) => {
        setValidateBankAccount(false);
        const newError = {
          ...directDebitErrors,
          ['bankValidation']:
            t('customer-direct-debit-page.validation-failed') + error
        };
        setDirectDebitErrors(newError);
        !externalLoadingPanel && setLoading(false);
        onDetailsValidated(false);
      };

      new PaymentApi().validateDirectDebit(
        directDebitDetails,
        token,
        successCallback,
        failureCallback,
        controller.signal
      );
    };

    if (validateBankAccount === true) {
      validateBankDetails();
    }

    return () => {
      controller.abort();
    };
  }, [validateBankAccount]);

  const validateDirectDebit = () => {
    let isValid = true;

    const errors: DirectDebitErrors = {};

    const sortCodeLength = 6;
    const accountNumberLength = 8;
    const accountNameCharLimit = 50;

    if (CommonValidation.isEmpty(accountHolderName)) {
      errors.accountHolderName = t(
        'customer-direct-debit-page.name-on-account-required'
      );
      isValid = false;
    } else {
      if (
        !CommonValidation.isValidLength(accountHolderName, accountNameCharLimit)
      ) {
        errors.accountHolderName = t(
          'customer-direct-debit-page.name-length-exceeded',
          { accountNameCharLimit: accountNameCharLimit }
        );
        isValid = false;
      }
    }

    if (
      CommonValidation.isEmpty(sortCode1) ||
      CommonValidation.isEmpty(sortCode2) ||
      CommonValidation.isEmpty(sortCode3)
    ) {
      errors.sortCode = t('customer-direct-debit-page.sort-code-required');
      isValid = false;
    } else {
      const sortCode = `${sortCode1}${sortCode2}${sortCode3}`;

      if (!CommonValidation.isValidNumeric(sortCode)) {
        errors.sortCode = t('customer-direct-debit-page.sort-code-invalid');
        isValid = false;
      } else if (sortCode.length !== sortCodeLength) {
        errors.sortCode = t('customer-direct-debit-page.sort-code-length', {
          sortCodeLength: sortCodeLength
        });
        isValid = false;
      }
    }

    if (!CommonValidation.isEmpty(accountNumber)) {
      if (!CommonValidation.isValidNumeric(accountNumber)) {
        errors.accountNumber = t(
          'customer-direct-debit-page.account-number-invalid'
        );
        isValid = false;
      } else if (accountNumber?.toString().length !== accountNumberLength) {
        errors.accountNumber = t(
          'customer-direct-debit-page.account-number-length',
          { accountNumberLength: accountNumberLength }
        );
        isValid = false;
      }
    } else {
      errors.accountNumber = t(
        'customer-direct-debit-page.account-number-required'
      );
      isValid = false;
    }
    if (
      showPreferredPaymentDay() === true &&
      (CommonValidation.isEmpty(selectedPaymentDay) ||
        SelectInputHelper.isNullValue(selectedPaymentDay))
    ) {
      isValid = false;
      errors.paymentDay = t(
        'customer-direct-debit-page.preferred-payment-date-required'
      );
    }

    setDirectDebitErrors(errors);

    return isValid;
  };

  useEffect(() => {
    if (validate === true) {
      !externalLoadingPanel && setLoading(true);
      const isValid = validateDirectDebit();
      if (isValid) {
        setValidateBankAccount(true);
      } else {
        !externalLoadingPanel && setLoading(false);
        onDetailsValidated(false);
      }
    }
  }, [validate]);

  useEffect(() => {
    setLoading(showLoading);
  }, [showLoading]);

  const onDetailsValidated = (isValid: boolean) => {
    onValidated(isValid);
  };
  const { t } = useTranslation();

  return (
    <LoadingContainer
      loading={loading}
      message={loadingMessage || t('customer-direct-debit-page.processing')}>
      <div data-testid='container-form' className='form-left-border'>
        <div className='control-label'>
          {t('customer-direct-debit-page.name-on-account')}
        </div>
        <div className='control'>
          <TextInput
            name='account-holder-name'
            placeholder={t('customer-direct-debit-page.name-on-account')}
            className='input-text w250'
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              onChangeDetail(e, 'accountHolderName')
            }
            value={accountHolderName}
            error={directDebitErrors?.accountHolderName || ''}
          />
        </div>

        <div className='flex-row'>
          <div className='inline-control'>
            <div className='control-label'>
              {t('customer-direct-debit-page.sort-code')}
            </div>
            <div className='control input-text'>
              <SortCodeInput
                label=''
                value1={sortCode1 || ''}
                value2={sortCode2 || ''}
                value3={sortCode3 || ''}
                error={directDebitErrors?.sortCode || ''}
                onChange={(e: React.FormEvent<HTMLInputElement>, key: string) =>
                  onChangeSortCode(e, key)
                }
              />
            </div>
          </div>

          <div className='inline-control'>
            <div className='control-label'>
              {t('customer-direct-debit-page.account-number')}
            </div>
            <div className='control'>
              <TextInput
                name='account-number'
                placeholder={t('customer-direct-debit-page.account-number')}
                className='input-text small'
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onChangeAccountNumber(e, 'accountNumber')
                }
                value={accountNumber || ''}
                maxLength={8}
                error={directDebitErrors?.accountNumber || ''}
              />
            </div>
          </div>
        </div>

        <div className='error-style'>{directDebitErrors.bankValidation}</div>
        {showPreferredPaymentDay() === true && (
          <>
            <div className='control-label'>
              {t('customer-direct-debit-page.preferred-payment-date')}
            </div>
            <div className='control'>
              <SelectInput
                name='payment-day'
                loadingText={t('customer-direct-debit-page.loading-text')}
                value={selectedPaymentDay}
                options={lookUpDays}
                onChange={onPaymentDayChange}
                className='input-text w250'
                forceDefault
                defaultIfOneOption
                addDefaultOption
                error={directDebitErrors.paymentDay}
              />
              {productChannel != ProductChannel.Insurance && (
                <span className='info-text'>
                  {t('customer-direct-debit-page.info-text')}
                </span>
              )}
            </div>
          </>
        )}

        {productChannel != ProductChannel.Insurance && (
          <>
            <Accordion
              className='m2-top m1-bottom'
              expanded={expanded}
              onChange={() => setExpanded(!expanded)}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls='vehiclePaneld-content'
                id='vehiclePaneld-header'>
                <p className={'p1'}>
                  {t('customer-direct-debit-page.direct-debit-gurantee')}
                </p>
              </AccordionSummary>
              <AccordionDetails>
                <div className='flex-row space-between m1-bottom'>
                  <p className='bold'>The Direct Debit Guarantee</p>
                  <div>
                    <img src={directDebitLogo} alt='Direct Debit Logo' />
                  </div>
                </div>
                <p className='m1-bottom'>
                  The Guarantee is offered by all banks and building societies
                  that accept instructions to pay Direct Debits.
                </p>
                <p className='m1-bottom cms'>
                  {`If there are any changes to the amount, date or frequency of your Direct Debit ${directDebitPayee} will notify you 10 working days in advance of your account being debited or as otherwise agreed. If you request ${directDebitPayee} to collect a payment, confirmation of the amount and date will be given to you at the time of the request.`}
                </p>
                <p className='m1-bottom cms'>
                  {`If an error is made in the payment of your Direct Debit, by ${directDebitPayee} or your bank or building society, you are entitled to a full and immediate refund of the amount paid from your bank or building society.`}
                </p>
                <p className='m1-bottom cms'>
                  {`If you receive a refund you are not entitled to, you must pay it back when ${directDebitPayee} asks you to.`}
                </p>
                <p className='m1-bottom'>
                  You can cancel a Direct Debit at any time by simply contacting
                  your bank or building society. Written confirmation may be
                  required. Please also notify us.
                </p>
              </AccordionDetails>
            </Accordion>
            <div className='m2-top'>
              <Checkbox
                id='direct-debit-agreement1'
                checked={agreedDirectDebit}
                text={
                  agreeDirectDebitTerms ||
                  t('customer-direct-debit-page.direct-debit-agreement1')
                }
                onChange={onDirectDebitAgreementChange}
              />
              {hasMonthly && (
                <Checkbox
                  id='direct-debit-agreement2'
                  checked={agreedDirectDebitDueDate}
                  text={
                    agreeDirectDebitDueDateTerms ||
                    t('customer-direct-debit-page.direct-debit-agreement2')
                  }
                  onChange={onDirectDebitDueDateConfirmationChange}
                />
              )}
              {hasSubscription && (
                <Checkbox
                  id='direct-debit-agreement3'
                  checked={agreedSubscriptionPayment}
                  text={t('customer-direct-debit-page.direct-debit-agreement3')}
                  onChange={onAgreedSubscriptionPaymentChange}
                />
              )}

              {displaySendDDGinWritingTerms && (
                <Checkbox
                  id='ddg-send-in-writing'
                  checked={agreedSendDDGInWriting}
                  text={t('customer-direct-debit-page.ddg-send-in-writing')}
                  onChange={onAgreedSendDDGInWritingChange}
                />
              )}
            </div>
            <PaymentSummary
              products={products.filter(
                (p) =>
                  p.selectedPaymentMethod === PaymentMethod.Monthly ||
                  p.selectedPaymentMethod === PaymentMethod.Subscription
              )}
              hasSubscription={hasSubscription}
              hasMonthly={hasMonthly}
              displayCustomerFriendlyName={displayCustomerFriendlyName ?? false}
              productChannel={productChannel}
              culture={culture}
            />
          </>
        )}
      </div>
    </LoadingContainer>
  );
};

export default DirectDebit;
